import React, { useRef, useState } from "react";
import { Camera, LocateFixed } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { GpsRequiredDialog } from "./GpsRequiredDialog";
import { compressImage, blobToDataUrl } from "../lib/imageCompress";
import { getStrictPosition, warmUpGps } from "../lib/gps";
import { newClientRef } from "../lib/offline";
import { api, errMsg } from "../lib/api";

/**
 * Phase 132 — camera button that only hands back a photo WITH a real GPS fix.
 *
 * Tap -> location warm-up + camera. When the photo comes back we take the device position; if location is
 * off / denied / unavailable the photo is discarded and <GpsRequiredDialog /> is shown. Otherwise the photo is
 * compressed (Phase 130) and `onCapture(photo)` receives
 *   { file, previewUrl, filename, lat, lng, accuracy, captured_at, client_ref }
 * The parent decides whether to upload immediately (standalone "Add GPS photo") or hold it for the visit drawer.
 */
export function GpsPhotoCapture({ label = "Add GPS photo", onCapture, disabled, busy, variant = "outline", size = "sm",
  className = "", testId = "gps-photo" }) {
  const inputRef = useRef(null);
  const [stage, setStage] = useState(null);          // null | "locating" | "processing"
  const [gpsDialog, setGpsDialog] = useState(false);

  const openCamera = () => {
    warmUpGps();
    inputRef.current?.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Only image files are allowed");
    setStage("locating");
    let gps;
    try {
      gps = await getStrictPosition();
    } catch (err) {
      setStage(null);
      setGpsDialog(true);                              // block: no GPS -> no photo
      return;
    }
    setStage("processing");
    try {
      const shrunk = await compressImage(file);
      const blob = shrunk.blob || file;
      const previewUrl = await blobToDataUrl(blob);
      const filename = (shrunk.filename || file.name || "photo.jpg").replace(/\.[^.]+$/, "") + ".jpg";
      await onCapture?.({ file: shrunk.file || file, previewUrl, filename, lat: gps.lat, lng: gps.lng, accuracy: gps.accuracy,
        captured_at: new Date().toISOString(), client_ref: newClientRef() });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setStage(null);
    }
  };

  const working = !!stage || busy;
  return (
    <>
      <Button type="button" variant={variant} size={size} className={className} disabled={disabled || working}
        onClick={openCamera} data-testid={`${testId}-button`}>
        {stage === "locating" ? <LocateFixed className="h-4 w-4 mr-1.5 animate-pulse" /> : <Camera className="h-4 w-4 mr-1.5" />}
        {stage === "locating" ? "Getting GPS location..." : stage === "processing" || busy ? "Saving photo..." : label}
      </Button>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile}
        data-testid={`${testId}-input`} />
      <GpsRequiredDialog open={gpsDialog} onOpenChange={setGpsDialog}
        onRetry={() => { setGpsDialog(false); setTimeout(openCamera, 50); }} testId={`${testId}-gps-required`} />
    </>
  );
}

/** Multipart upload of one captured GPS photo to the property (server re-validates the fix). */
export async function uploadPropertyPhoto(code, propertyId, photo) {
  const fd = new FormData();
  fd.append("file", photo.file, photo.filename || photo.file?.name || "photo.jpg");
  fd.append("lat", String(photo.lat));
  fd.append("lng", String(photo.lng));
  if (photo.accuracy != null) fd.append("accuracy", String(photo.accuracy));
  if (photo.captured_at) fd.append("captured_at", photo.captured_at);
  if (photo.client_ref) fd.append("client_ref", photo.client_ref);
  const r = await api.post(`/t/${code}/field/property-photos/${propertyId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  return r.data.photo;
}

/** Serialise held photos for the offline queue (data URL + GPS; replayed with the same client_ref). */
export const heldToQueuePhotos = (held) => (held || []).map((p) => ({
  data: p.previewUrl, filename: p.filename || "visit-photo.jpg", lat: p.lat, lng: p.lng, accuracy: p.accuracy,
  captured_at: p.captured_at, client_ref: p.client_ref,
}));
